import classNames from "classnames";
import useMediaQuery from "hooks/useMediaQuery";
import React from "react";
import { connect, ConnectedProps } from "react-redux";
import {
	Area,
	CartesianGrid,
	ComposedChart,
	LabelList,
	Legend,
	Line,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { Dispatch } from "redux";
import { IStore } from "redux/store";

const renderLineLabel = (props: any) => {
	const { x, y, value } = props;
	return (
		<text x={x} y={y} dy={-10} fill="#666" fontSize={12} textAnchor="middle">
			{value}
		</text>
	);
};

const CustomizedAxisTick = (props: any) => {
	const { x, y, payload } = props;
	// Split the label string by the newline character
	const [month, year] = payload.value.split(" ");

	return (
		<g transform={`translate(${x},${y})`}>
			<text
				x={0}
				y={0}
				dy={16}
				textAnchor="middle"
				fill="#6b7280"
				fontSize={12}
			>
				{/* Render month and year on separate lines using tspan */}
				<tspan textAnchor="middle" x="0">
					{month}
				</tspan>
				<tspan textAnchor="middle" x="0" dy="15">
					{year}
				</tspan>
			</text>
		</g>
	);
};

const CustomTooltip = ({ active, payload, label }: any) => {
	if (active && payload && payload.length) {
		// The payload can contain multiple entries for the same dataKey (e.g., from Area and Line).
		// We create a unique list based on the 'name' prop to avoid duplicates.
		const uniquePayload = payload
			.reduce((acc: any[], current: any) => {
				if (!acc.find((item) => item.name === current.name)) {
					acc.push(current);
				}
				return acc;
			}, [])
			.filter((item: any) => item?.stroke !== "none");

		return (
			<div className="p-3 bg-white border border-gray-200 rounded-lg shadow-lg">
				<p className="font-medium text-gray-800 mb-2">{label}</p>
				{uniquePayload.map(
					(pld: any, index: number) =>
						pld.name && (
							<p key={index} style={{ color: pld.color }} className="text-sm">
								{`${pld.name}: ${pld.value.toLocaleString()}`}
							</p>
						)
				)}
			</div>
		);
	}

	return null;
};

const CustomLegend = (props: any) => {
	let { payload } = props;
	payload = payload.filter((data: any) => data?.payload?.name);
	return (
		<div className="flex justify-end items-center space-x-4">
			{payload.map(
				(entry: any, index: number) =>
					// We use the legendType property to filter out items we don't want in the legend
					entry.type !== "none" && (
						<div
							key={`item-${index}`}
							className="flex items-center space-x-2 cursor-pointer"
						>
							<div
								style={{
									width: 10,
									height: 10,
									borderRadius: "50%",
									backgroundColor: entry.color,
								}}
							></div>
							<span
								style={{ color: entry.color }}
								className="text-sm font-medium"
							>
								{entry.value}
							</span>
						</div>
					)
			)}
		</div>
	);
};

const renderRecdLabel = (props: any) => {
	const { x, y, value } = props;
	// Position the label above the point
	const dy = value === 0 ? -10 : -10;
	// For zero values, we want them just above the x-axis line
	const finalY = value === 0 ? y - 5 : y;

	return (
		<text
			x={x}
			y={finalY}
			dy={dy}
			fill="#666"
			fontSize={12}
			textAnchor="middle"
		>
			{value}
		</text>
	);
};

export interface IAreaGraph1Point {
	name: string;
	aeccRob: number;
	aeccRecd: number;
}

const areaGraph1Data: IAreaGraph1Point[] = [
	{ name: "Jan 2024", aeccRob: 20000, aeccRecd: 0 },
	{ name: "Feb 2024", aeccRob: 19700, aeccRecd: 0 },
	{ name: "Mar 2024", aeccRob: 19350, aeccRecd: 0 },
	{ name: "Apr 2024", aeccRob: 18900, aeccRecd: 0 },
	{ name: "May 2024", aeccRob: 18600, aeccRecd: 0 },
	{ name: "Jun 2024", aeccRob: 18300, aeccRecd: 0 },
	{ name: "Jul 2024", aeccRob: 17950, aeccRecd: 0 },
	{ name: "Aug 2024", aeccRob: 17650, aeccRecd: 0 },
	{ name: "Sep 2024", aeccRob: 17300, aeccRecd: 0 },
	{ name: "Oct 2024", aeccRob: 17000, aeccRecd: 0 },
	{ name: "Nov 2024", aeccRob: 16700, aeccRecd: 0 },
	{ name: "Dec 2024", aeccRob: 16400, aeccRecd: 0 },
	{ name: "Jan 2025", aeccRob: 16100, aeccRecd: 0 },
	{ name: "Feb 2025", aeccRob: 15800, aeccRecd: 0 },
	{ name: "Mar 2025", aeccRob: 15500, aeccRecd: 0 },
	{ name: "Apr 2025", aeccRob: 15200, aeccRecd: 0 },
	{ name: "May 2025", aeccRob: 14900, aeccRecd: 0 },
	{ name: "Jun 2025", aeccRob: 26600, aeccRecd: 12000 },
	{ name: "Jul 2025", aeccRob: 26300, aeccRecd: 0 },
];

type OwnProps = {};
type AreaGraph1Props = PropsFromRedux & OwnProps;
function AreaGraph1(props: AreaGraph1Props) {
	const lgScreen = 1024;
	const isWidthLessThanLgScreen = useMediaQuery(lgScreen);
	return (
		<div className="overflow-x-auto p-4 shadow-md border border-gray-300 rounded-md mt-4 w-full">
			<div className="w-full">
				<div className="relative">
					<h3 className="text-base lg:text-lg">
						Consumption & Receipt metric by tank type
					</h3>
					<h4 className="absolute text-blue-500 text-sm font-medium">
						Total AECC Received: 12K
					</h4>
				</div>
				<div className={classNames({ "w-[1000px]": isWidthLessThanLgScreen })}>
					<ResponsiveContainer
						className={"w-full"}
						width="100%"
						height={450}
						debounce={2}
					>
						<ComposedChart
							data={areaGraph1Data}
							margin={{
								top: 40,
								right: 30,
								bottom: 20,
								left: 0,
							}}
						>
							{/* Define SVG gradients for area fills */}
							<defs>
								<linearGradient id="colorRob" x1="0" y1="0" x2="0" y2="1">
									<stop offset="5%" stopColor="#808080" stopOpacity={0.4} />
									<stop offset="95%" stopColor="#808080" stopOpacity={0} />
								</linearGradient>
								<linearGradient id="colorRecd" x1="0" y1="0" x2="0" y2="1">
									<stop offset="5%" stopColor="#ffc658" stopOpacity={0.8} />
									<stop offset="95%" stopColor="#ffc658" stopOpacity={0.2} />
								</linearGradient>
							</defs>

							<CartesianGrid strokeDasharray="3 3" vertical={true} />
							<XAxis
								dataKey="name"
								padding={{ left: 30 }}
								axisLine={false}
								tickLine={false}
								interval={0}
								style={{ whiteSpace: "pre" }}
								tick={<CustomizedAxisTick />}
							/>
							<YAxis
								domain={[0, 30000]}
								tick={{ fontSize: 12, fill: "#6b7280" }}
								axisLine={false}
								tickLine={false}
							/>

							<Tooltip content={<CustomTooltip />} />
							<Legend
								verticalAlign="top"
								align="right"
								iconType="wye"
								iconSize={10}
								wrapperStyle={{ top: 0, right: 20 }}
								formatter={(value, entry) => {
									const { color } = entry;
									return <span style={{ color }}>{value}</span>;
								}}
								content={<CustomLegend />}
							/>

							<ReferenceLine y={20000} stroke="#ef4444" strokeDasharray="4 4" />

							<Area
								name="AECC RECD"
								type="step"
								dataKey="aeccRecd"
								stroke="#ffc658"
								strokeWidth={2}
								fill="url(#colorRecd)"
								dot={false}
							>
								<LabelList dataKey="aeccRecd" content={renderRecdLabel} />
							</Area>
							<Area
								// This area provides the grey fill under the line
								type="linear"
								dataKey="aeccRob"
								stroke="none"
								fill="url(#colorRob)"
							/>
							<Line
								name="AECC ROB"
								type="linear"
								dataKey="aeccRob"
								stroke="#5a6472"
								strokeWidth={2}
								dot={{ stroke: "#5a6472", strokeWidth: 2, r: 4, fill: "white" }}
								activeDot={{ r: 6, strokeWidth: 2, fill: "#5a6472" }}
							>
								<LabelList dataKey="aeccRob" content={renderLineLabel} />
							</Line>
						</ComposedChart>
					</ResponsiveContainer>
				</div>
			</div>
		</div>
	);
}
function mapStateToProps(store: IStore, ownProps: OwnProps) {
	return {};
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(AreaGraph1);
